import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import qs from 'qs';
import apiClient from '@/lib/apiClient';
import { paths } from '../../generated/supplier-experience-api';

// TODO: check which fields are actually nullable/optional
export interface TakerHistoryStatsData {
  acceptedAprWeightedAvg: number;
  acceptedDiscountWeightedAvg: number;
  acceptedDiscountedAmount: number;
  acceptedDpeWeightedAvg: number;
  acceptedEarn: number;
  acceptedInvoiceAmount: number;
  acceptedInvoiceCount: number;
  awardedAprWeightedAvg: number | null;
  awardedDiscountWeightedAvg: number | null;
  awardedDiscountedAmount: number;
  awardedDpeWeightedAvg: number | null;
  awardedEarn: number;
  awardedInvoiceAmount: number;
  awardedInvoiceCount: number;
  clearedAprWeightedAvg: number;
  clearedDiscountWeightedAvg: number;
  clearedDiscountWeightedSum: number;
  clearedDiscountedAmount: number;
  clearedDpeWeightedAvg: number;
  clearedEarn: number;
  clearedInvoiceAmount: number;
  clearedInvoiceCount: number;
  coveringInvoiceAmount: number;
  coveringInvoiceCount: number;
  currency: string;
  matchedAdjustmentAmount: number;
  matchedAdjustmentCount: number;
  maxAcceptedApr: number;
  maxAcceptedDiscount: number;
  maxAcceptedDpe: number;
  maxAwardedApr: number | null;
  maxAwardedDiscount: number | null;
  maxAwardedDpe: number | null;
  maxClearedApr: number;
  maxClearedDiscount: number;
  maxClearedDpe: number;
  minAcceptedApr: number;
  minAcceptedDiscount: number;
  minAcceptedDpe: number;
  minAwardedApr: number | null;
  minAwardedDiscount: number | null;
  minAwardedDpe: number | null;
  minClearedApr: number;
  minClearedDiscount: number;
  minClearedDpe: number;
  notAcceptedAprWeightedAvg: number | null;
  notAcceptedDiscountWeightedAvg: number | null;
  notAcceptedDiscountedAmount: number;
  notAcceptedDpeWeightedAvg: number | null;
  notAcceptedEarn: number;
  notAcceptedInvoiceAmount: number;
  notAcceptedInvoiceCount: number;
  totalAdjustmentAmount: number;
  totalAdjustmentCount: number;
  totalInvoiceCount: number;
  unmatchedAdjustmentAmount: number;
  unmatchedAdjustmentCount: number;
}

export interface HistoryStats {
  clearedInvoiceAmount: number;
  clearedEarn: number;
  clearedDiscountWeightedSum: number;
  clearedDiscountWeightedAvg: number | null;
  minimumClearPayDate: string | null;
  minimumClearDepositDate: string | null;
  latestClearPayDate: string | null;
  latestClearDepositDate: string | null;
}

// https://fss-gateway.c2fo.com/usercast/api/c2fo/user/taker/history/stats?currency=USD&filter=%7B%22payDate__between%22:%5B%222023-04-01,2023-06-30%22%5D%7D

export interface TakerHistoryStatsArgs {
  startDate: string;
  endDate: string;
  currency: string;
  excludePending: boolean;
  makerOrganizationUuids?: string[];
  dateType?: 'payDate' | 'depositDate';
}

export type SEASupplierMarketClearsStatsParams = paths['/supplier-market-clears/stats']['get']['parameters']['query'];
export type SEASupplierMarketClearsStatsResponse =
  paths['/supplier-market-clears/stats']['get']['responses']['200']['content']['application/json'];

const fetchTakerHistoryStats = async ({
  startDate,
  endDate,
  currency,
  makerOrganizationUuids,
  excludePending,
  dateType = 'depositDate', // default to deposit date, updating fallback from pay date to deposit date now that good funds date project is fully released
}: TakerHistoryStatsArgs) => {
  const searchParams = qs.stringify(
    {
      toCurrency: currency,
      // use deposit date as date range if dateType is depositDate, otherwise use pay date
      ...(startDate && (dateType === 'depositDate' ? { startDepositDate: startDate } : { startPayDate: startDate })),
      ...(endDate && (dateType === 'depositDate' ? { endDepositDate: endDate } : { endPayDate: endDate })),
      ...(excludePending !== undefined && { isPendingClear: excludePending ? 'false' : 'true' }),
      ...(makerOrganizationUuids &&
        makerOrganizationUuids.length > 0 && { makerOrganizationUuids: [...new Set(makerOrganizationUuids)] }),
    } satisfies SEASupplierMarketClearsStatsParams,
    { arrayFormat: 'repeat' } // 'repeat' format produces makerOrganizationUuids=id1&makerOrganizationUuids=id2, which is parsed correctly by both Express 4 (qs/extended) and Express 5 (Node querystring/simple) without any server-side override
  );

  const supplierMarketClearsResponse = await apiClient
    .get(`api/supplier-experience/supplier-market-clears/stats`, {
      searchParams: searchParams,
    })
    .json<SEASupplierMarketClearsStatsResponse>();

  const seaData = supplierMarketClearsResponse?.data;
  if (!seaData) {
    return {
      clearedInvoiceAmount: 0,
      clearedEarn: 0,
      clearedDiscountWeightedSum: 0,
      clearedDiscountWeightedAvg: 0,
      minimumClearPayDate: null,
      minimumClearDepositDate: null,
      latestClearPayDate: null,
      latestClearDepositDate: null,
    };
  }

  // Transform SEA response to match HistoryStats interface
  // Calculate clearedDiscountWeightedSum from clearedDiscountWeightedAvg and clearedInvoiceAmount
  const clearedDiscountWeightedSum =
    seaData.clearedDiscountWeightedAvg && seaData.clearedInvoiceAmount
      ? seaData.clearedDiscountWeightedAvg * seaData.clearedInvoiceAmount
      : 0;

  return {
    clearedInvoiceAmount: seaData.clearedInvoiceAmount || 0,
    clearedEarn: seaData.clearedEarn || 0,
    clearedDiscountWeightedSum,
    clearedDiscountWeightedAvg: seaData.clearedDiscountWeightedAvg || null,
    minimumClearPayDate: seaData.minimumClearPayDate || null,
    minimumClearDepositDate: seaData.minimumClearDepositDate || null,
    latestClearPayDate: seaData.latestClearPayDate || null,
    latestClearDepositDate: seaData.latestClearDepositDate || null,
  };
};

const useTakerHistoryStats = (params: TakerHistoryStatsArgs, options?: Partial<UseQueryOptions<HistoryStats>>) => {
  return useQuery({
    // This is intentionally not useSuspenseQuery, loading states are handled in consumers.
    queryKey: ['taker-history-stats', params],
    queryFn: () => fetchTakerHistoryStats(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

export default useTakerHistoryStats;
